import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  SparklesIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

type AiMode = 'continue' | 'suggest' | 'edit' | 'dialogue' | 'describe' | null;

const ChapterEditor: React.FC = () => {
  const { id, chapterId } = useParams<{ id: string; chapterId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('planned');
  const [wordCount, setWordCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // AI panel state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMode, setAiMode] = useState<AiMode>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const [aiInstruction, setAiInstruction] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [characters, setCharacters] = useState<any[]>([]);
  const [selectedCharacterId, setSelectedCharacterId] = useState('');

  // Scene beats panel
  const [scenesOpen, setScenesOpen] = useState(false);
  const [scenes, setScenes] = useState<any[]>([]);

  const computeWords = (text: string) =>
    text
      .trim()
      .split(/\s+/)
      .filter((w) => w.length > 0).length;

  const fetchChapter = useCallback(async () => {
    try {
      const [chapterRes, storyRes] = await Promise.all([
        axios.get(`/stories/${id}/chapters/${chapterId}`),
        axios.get(`/stories/${id}`),
      ]);
      setTitle(chapterRes.data.title || '');
      setContent(chapterRes.data.content || '');
      setStatus(chapterRes.data.status || 'planned');
      setWordCount(chapterRes.data.wordCount || 0);
      setCharacters(storyRes.data.characters || []);
    } catch {
      toast.error(t('stories.loadError', 'Failed to load'));
      navigate(`/stories/${id}`);
    } finally {
      setLoading(false);
    }
  }, [id, chapterId, navigate, t]);

  const fetchScenes = useCallback(async () => {
    try {
      const res = await axios.get(`/stories/${id}/chapters/${chapterId}/scenes`);
      setScenes(res.data);
    } catch {
      /* ignore */
    }
  }, [id, chapterId]);

  useEffect(() => {
    fetchChapter();
    fetchScenes();
  }, [fetchChapter, fetchScenes]);

  const handleContentChange = (text: string) => {
    setContent(text);
    setWordCount(computeWords(text));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`/stories/${id}/chapters/${chapterId}`, {
        title: title || undefined,
        content,
        status,
      });
      setDirty(false);
      toast.success(t('stories.saved', 'Saved'));
    } catch {
      toast.error(t('stories.saveError', 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  // Auto-save on Ctrl+S
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (dirty) save();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dirty, content, title, status]);

  // Track text selection
  const handleSelect = () => {
    const sel = window.getSelection()?.toString() || '';
    setSelectedText(sel);
  };

  // AI actions
  const runAi = async (mode: AiMode) => {
    if (!mode) return;
    setAiMode(mode);
    setAiLoading(true);
    setAiResult('');
    setAiOpen(true);

    try {
      let res;
      switch (mode) {
        case 'continue':
          res = await axios.post(`/stories/${id}/chapters/${chapterId}/ai/continue`, {
            mode: 'continue',
            instructions: aiInstruction || undefined,
          });
          break;
        case 'suggest':
          res = await axios.post(`/stories/${id}/chapters/${chapterId}/ai/suggest`, {
            mode: 'suggest',
          });
          break;
        case 'edit':
          if (!selectedText) {
            toast.error(t('stories.ai.selectTextFirst', 'Select text in the editor first'));
            setAiLoading(false);
            return;
          }
          res = await axios.post(`/stories/${id}/chapters/${chapterId}/ai/edit`, {
            mode: 'edit',
            selectedText,
            instruction: aiInstruction || 'Improve this passage',
          });
          break;
        case 'dialogue':
          if (!selectedCharacterId) {
            toast.error(t('stories.ai.selectCharacter', 'Select a character first'));
            setAiLoading(false);
            return;
          }
          res = await axios.post(`/stories/${id}/chapters/${chapterId}/ai/dialogue`, {
            mode: 'dialogue',
            characterId: selectedCharacterId,
            context: aiInstruction || undefined,
          });
          break;
        case 'describe':
          if (!aiInstruction) {
            toast.error(t('stories.ai.enterInstruction', 'Enter a description instruction'));
            setAiLoading(false);
            return;
          }
          res = await axios.post(`/stories/${id}/chapters/${chapterId}/ai/describe`, {
            mode: 'describe',
            instruction: aiInstruction,
          });
          break;
      }
      setAiResult(res?.data?.content || '');
    } catch {
      toast.error(t('stories.ai.error', 'AI request failed'));
    } finally {
      setAiLoading(false);
    }
  };

  const insertAiResult = () => {
    if (!aiResult) return;
    if (aiMode === 'edit' && selectedText) {
      // Replace selected text
      const idx = content.indexOf(selectedText);
      if (idx !== -1) {
        const newContent =
          content.slice(0, idx) + aiResult + content.slice(idx + selectedText.length);
        handleContentChange(newContent);
      } else {
        handleContentChange(content + '\n\n' + aiResult);
      }
    } else {
      handleContentChange(content + (content ? '\n\n' : '') + aiResult);
    }
    setAiResult('');
    setAiInstruction('');
    toast.success(t('stories.ai.inserted', 'Inserted'));
  };

  if (loading) {
    return (
      <div
        className="h-full flex items-center justify-center"
        style={{ background: 'var(--ds-bg-primary)' }}
      >
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2"
          style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--ds-bg-primary)' }}>
      {/* Header */}
      <header
        className="flex items-center gap-3 px-4 py-3 border-b"
        style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)' }}
      >
        <button onClick={() => navigate(`/stories/${id}`)}>
          <ArrowLeftIcon className="w-5 h-5" style={{ color: 'var(--ds-text-primary)' }} />
        </button>
        <input
          type="text"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setDirty(true);
          }}
          placeholder={t('stories.chapterTitle', 'Chapter title...')}
          className="flex-1 bg-transparent text-lg font-semibold focus:outline-none"
          style={{ color: 'var(--ds-text-primary)' }}
        />
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setDirty(true);
          }}
          className="text-xs px-2 py-1 rounded-lg border bg-transparent"
          style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
        >
          {['planned', 'in_progress', 'draft', 'revised', 'final'].map((s) => (
            <option key={s} value={s}>
              {s.replace('_', ' ')}
            </option>
          ))}
        </select>
        <button
          onClick={() => setScenesOpen(!scenesOpen)}
          className={`p-2 rounded-lg transition-colors ${scenesOpen ? 'bg-primary-500/20 text-primary-400' : ''}`}
          style={!scenesOpen ? { color: 'var(--ds-text-secondary)' } : {}}
          title={t('stories.scenes.toggle', 'Scene Beats')}
        >
          <ListBulletIcon className="w-5 h-5" />
        </button>
        <button
          onClick={() => setAiOpen(!aiOpen)}
          className={`p-2 rounded-lg transition-colors ${aiOpen ? 'bg-primary-500/20 text-primary-400' : ''}`}
          style={!aiOpen ? { color: 'var(--ds-text-secondary)' } : {}}
          title={t('stories.ai.toggle', 'AI Assistant')}
        >
          <SparklesIcon className="w-5 h-5" />
        </button>
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="px-4 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm rounded-lg font-medium transition-colors"
        >
          {saving ? '...' : t('common.save', 'Save')}
        </button>
      </header>

      {/* Scene beats panel (collapsible top section) */}
      {scenesOpen && (
        <div
          className="border-b px-4 py-3 max-h-48 overflow-y-auto"
          style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold" style={{ color: 'var(--ds-text-secondary)' }}>
              {t('stories.scenes.beatsTitle', 'Scene Beats')}
            </span>
            <button onClick={() => setScenesOpen(false)}>
              <XMarkIcon className="w-4 h-4" style={{ color: 'var(--ds-text-secondary)' }} />
            </button>
          </div>
          {scenes.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
              {t('stories.scenes.emptyBeats', 'No scenes planned for this chapter.')}
            </p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {scenes.map((scene: any, idx: number) => (
                <div
                  key={scene.id}
                  className="shrink-0 w-48 rounded-lg p-2.5 text-xs"
                  style={{
                    background: 'var(--ds-bg-primary)',
                    border: '1px solid var(--ds-border)',
                    color: 'var(--ds-text-primary)',
                  }}
                >
                  <div className="font-medium mb-1">
                    {t('stories.scenes.beatLabel', 'Beat')} {idx + 1}
                  </div>
                  <div className="line-clamp-2" style={{ color: 'var(--ds-text-secondary)' }}>
                    {scene.summary || t('stories.scenes.untitled', 'Untitled scene')}
                  </div>
                  {scene.emotionalArc && (
                    <div
                      className="mt-1 text-[10px] italic"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    >
                      {scene.emotionalArc}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor */}
        <div className={`flex-1 overflow-hidden ${aiOpen ? 'md:w-[65%]' : 'w-full'}`}>
          <textarea
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            onSelect={handleSelect}
            className="w-full h-full p-6 bg-transparent resize-none focus:outline-none text-base leading-relaxed"
            style={{ color: 'var(--ds-text-primary)' }}
            placeholder={t('stories.startWriting', 'Start writing...')}
          />
        </div>

        {/* AI Panel */}
        {aiOpen && (
          <div
            className="w-full md:w-[35%] border-l flex flex-col overflow-hidden"
            style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)' }}
          >
            {/* Panel header */}
            <div
              className="flex items-center justify-between px-4 py-3 border-b"
              style={{ borderColor: 'var(--ds-border)' }}
            >
              <div className="flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-primary-400" />
                <span className="text-sm font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
                  {t('stories.ai.title', 'AI Assistant')}
                </span>
              </div>
              <button onClick={() => setAiOpen(false)}>
                <XMarkIcon className="w-4 h-4" style={{ color: 'var(--ds-text-secondary)' }} />
              </button>
            </div>

            {/* Quick actions */}
            <div className="px-4 py-3 space-y-2">
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    { mode: 'continue' as AiMode, label: t('stories.ai.continue', 'Continue') },
                    { mode: 'suggest' as AiMode, label: t('stories.ai.suggest', 'Suggest') },
                    { mode: 'describe' as AiMode, label: t('stories.ai.describe', 'Describe') },
                    {
                      mode: 'edit' as AiMode,
                      label: t('stories.ai.editSelection', 'Edit Selection'),
                    },
                    { mode: 'dialogue' as AiMode, label: t('stories.ai.dialogue', 'Dialogue') },
                  ] as const
                ).map((action) => (
                  <button
                    key={action.mode}
                    onClick={() => runAi(action.mode)}
                    disabled={aiLoading}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border hover:border-primary-500 hover:text-primary-400 disabled:opacity-50"
                    style={{
                      borderColor: 'var(--ds-border)',
                      color: 'var(--ds-text-secondary)',
                    }}
                  >
                    {action.label}
                  </button>
                ))}
              </div>

              {/* Character selector for dialogue */}
              {characters.length > 0 && (
                <select
                  value={selectedCharacterId}
                  onChange={(e) => setSelectedCharacterId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border bg-transparent text-xs"
                  style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
                >
                  <option value="">
                    {t('stories.ai.selectCharacterOption', '— Character for dialogue —')}
                  </option>
                  {characters.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}

              {/* Instruction input */}
              <input
                type="text"
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                placeholder={t('stories.ai.instructionPlaceholder', 'Instructions (optional)...')}
                className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
                style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !aiLoading) {
                    runAi(aiMode || 'continue');
                  }
                }}
              />

              {selectedText && (
                <div className="text-[11px] px-2 py-1 rounded bg-yellow-500/10 text-yellow-400 truncate">
                  {t('stories.ai.selected', 'Selected')}: "{selectedText.slice(0, 80)}
                  {selectedText.length > 80 ? '...' : ''}"
                </div>
              )}
            </div>

            {/* Result area */}
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {aiLoading && (
                <div className="flex items-center justify-center py-12">
                  <div
                    className="animate-spin rounded-full h-6 w-6 border-b-2"
                    style={{ borderColor: 'var(--ds-accent, #8b5cf6)' }}
                  />
                </div>
              )}

              {!aiLoading && aiResult && (
                <div className="space-y-3">
                  <div
                    className="text-sm leading-relaxed whitespace-pre-wrap rounded-xl p-4"
                    style={{
                      background: 'var(--ds-bg-primary)',
                      color: 'var(--ds-text-primary)',
                      border: '1px solid var(--ds-border)',
                    }}
                  >
                    {aiResult}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={insertAiResult}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm rounded-lg font-medium transition-colors"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                      {t('stories.ai.insert', 'Insert')}
                    </button>
                    <button
                      onClick={() => {
                        setAiResult('');
                        setAiInstruction('');
                      }}
                      className="px-4 py-2 rounded-lg text-sm border"
                      style={{
                        borderColor: 'var(--ds-border)',
                        color: 'var(--ds-text-secondary)',
                      }}
                    >
                      {t('stories.ai.discard', 'Discard')}
                    </button>
                    <button
                      onClick={() => runAi(aiMode)}
                      className="p-2 rounded-lg border"
                      style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
                      title={t('stories.ai.retry', 'Retry')}
                    >
                      <ArrowPathIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {!aiLoading && !aiResult && (
                <p
                  className="text-center text-sm py-8"
                  style={{ color: 'var(--ds-text-secondary)' }}
                >
                  {t(
                    'stories.ai.hint',
                    'Click a quick action or type instructions and press Enter.',
                  )}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer
        className="flex items-center justify-between px-6 py-2 border-t text-xs"
        style={{ borderColor: 'var(--ds-border)', color: 'var(--ds-text-secondary)' }}
      >
        <span>
          {wordCount.toLocaleString()} {t('stories.words', 'words')}
        </span>
        {dirty && (
          <span className="text-yellow-400">{t('stories.unsaved', 'Unsaved changes')}</span>
        )}
      </footer>
    </div>
  );
};

export default ChapterEditor;
